import React from 'react';
import { observer } from 'mobx-react';
import packagesCss from './packages.css';
import { PACKAGE_ARCH, PACKAGE_TYPE } from 'dto/packages-dto';
import {
    BasicCard,
    BasicForm,
    FormItem,
    IBasicTableColumn,
    IconTextButton,
    ISelectItemProps,
    ITreeSelectDataSource,
    Label,
    notification,
    SelectFormItem,
    showConfirmDialog,
    TextAreaFieldItem,
    TextFieldItem,
    toSelectItemFn,
    TransferWithContentTable,
    Typography,
    useForm,
    SwitchSelectFormItem,
} from 'ui-lib';

import { useAuthStore, usePackagesStore, useSnapshotStore as useSnapshotStore, useStores, } from '../../../../store';
import { TDisplayPackageProps } from '../../../../store/packages-store';
import { manageSnapshotPermission, useSnapshotContext } from '.';
import { ViewSnapshotPackageComponent } from './snapshot-details';
import { isEqual, toNumber, without } from 'lodash';
import { confirmCancel } from '../../admin/utils';
import { useSpinnerComponent } from '../../components/loading-component';
import { OrganisationSelectItem } from 'pages/private/components/organisation-select';
import { AccountType, IAccount } from 'dto/access-management/account-dto';
import { getErrorMessage, isNullOrEmpty } from 'common-utils';
import { 
    IExternalSourceListProps as IExternalSourceListProps, 
    IMetaPackageInReleaseProps, 
    IRepositorySettingsProps, 
    ISnapshotProps, 
    METAPACKAGE_TYPE 
} from 'dto/software-snapshot-dto';
import { SearchableSelectFormItem } from '../../components/searchable-component';
import { hasContainsIgnoreCase } from 'common-utils/string-utils';
import {
    showDuplicatePackagesModal,
    showMissingPackageDependenciesModal,
    showPackagesWithDescriptionModal
} from 'pages/private/components/package-modal-components';
import { IDisplaySnapshotProps } from 'store/snapshot.store';
import { usePromiseMemo } from 'utils/common-utils';
import { RepositoryType } from 'dto/repository.dto';

function confirmDialog(content: string, action: () => void): void {
    showConfirmDialog({
        title: 'Confirmation',
        content: content,
        okText: 'OK',
        cancelText: 'Cancel',
        onOk: action,
    });
}

interface ISaveSnapshotProps {
    apiAccountId: number;
    label: string;
    description?: string;
    accountUuid: string;
    basedReleaseId: number;
    almReleaseId: number;
    vosReleaseId: number;
    scrReleaseId: number;
    selectedPackages: number[];
    useExternalSettings?: boolean;
    externalSourceList?: IExternalSourceListProps;
}

interface ISnapshotDetails {
    title?: string;
    name?: string;
    description?: string;
    changeableTargetPackages: number[];
    accountUuid?: string;
    defaultMetapackages: IMetaPackageInReleaseProps;
    viewOnly: boolean;
    handler: (options: ISaveSnapshotProps) => Promise<unknown>;
    basedReleaseId?: number;
    useExternalSettings?: boolean;
    externalSourceList?: IExternalSourceListProps;
}

interface IAddPackageProps extends Omit<ISnapshotDetails, 'handler' | 'viewOnly'> {
    loading: boolean;
    onSaveSnapshot: (options: ISaveSnapshotProps) => Promise<void>;
    onCancel: () => void;
}

interface IMetaPackageStates {
    source: IDisplaySnapshotProps[];
    selectedItem: number;
    onChange: (id: number) => void;
    packages: TDisplayPackageProps[];
    description?: string;
}

interface IMetaPackageComponentProps extends IMetaPackageStates {
    code: string;
    label: string;
}

export interface IViewOrModifySnapshotProps {
    snapshotId: number;
}

const AVAILABLE_PACKAGE_TYPES = [PACKAGE_TYPE.VAR, PACKAGE_TYPE.CLIENT];

const buildItemFn = toSelectItemFn('id', 'label');

const useMetaPackageStates = (type: METAPACKAGE_TYPE, metapackages: IMetaPackageInReleaseProps): IMetaPackageStates => {
    const snapshotStore = useSnapshotStore();
    const availableList = React.useMemo<IDisplaySnapshotProps[]>(() => snapshotStore.displayDPUSnapshots[type] ?? [], [snapshotStore.displayDPUSnapshots]);
    const [selectedMetaPackage, setSelectedMetaPackage] = React.useState<number>(() => metapackages[type]?.id);

    React.useEffect(() => {
        const defaultMetapackage = metapackages[type];
        if (defaultMetapackage) {
            setSelectedMetaPackage(defaultMetapackage.id);
            return;
        }
        setSelectedMetaPackage(snapshotStore.getLatestDPUSnapshotByType(type)?.id);
    }, [metapackages, snapshotStore.displayDPUSnapshots, type]);

    const packages = React.useMemo<(TDisplayPackageProps)[]>(() => {
        return snapshotStore.getPackagesBySnapshotId(selectedMetaPackage);
    }, [selectedMetaPackage]);

    return {
        source: availableList || [],
        selectedItem: selectedMetaPackage,
        onChange: setSelectedMetaPackage,
        packages: packages,
        description: (availableList.find(snapshot => snapshot.id === selectedMetaPackage))?.description,
    };
};
const trimExternalSourceList = (externalSourceList: IExternalSourceListProps): IExternalSourceListProps => {
    return {
        ...externalSourceList,
        repositorySettings: {
            ...externalSourceList.repositorySettings,
            target: externalSourceList.repositorySettings.target.trim(),
            distribution: externalSourceList.repositorySettings.distribution.trim(),
            component: externalSourceList.repositorySettings.component.trim(),
            options: externalSourceList.repositorySettings.options.map(option => option.trim()),
        },
        installPackages: externalSourceList.installPackages.map(pkg => pkg.trim()),
        removePackages: externalSourceList.removePackages.map(pkg => pkg.trim()),
    };
};
export const ViewOrModifySnapshot = observer((props: IViewOrModifySnapshotProps) => {
    const { snapshotId } = props;
    const context = useSnapshotContext();

    const packagesStore = usePackagesStore();
    const snapshotStore = useSnapshotStore();

    const { canEditSnapshot } = manageSnapshotPermission();
    const { SpinnerComponent: SpiningComponent, ...spinningStates } = useSpinnerComponent();

    const snapshotDetails = React.useMemo<ISnapshotDetails>(() => {
        const snapshot = snapshotStore.getSnapshot(snapshotId);
        if (snapshot) {
            return {
                title: 'Edit software release',
                name: snapshot.label,
                description: snapshot.description,
                defaultMetapackages: snapshotStore.getMetaPackagesInRelease(snapshot.id),
                changeableTargetPackages: snapshot.packageIds,
                basedReleaseId: snapshot.basedReleaseId,
                accountUuid: snapshot.accountUuid,
                useExternalSettings: snapshot.useExternalSettings ?? false,
                externalSourceList: snapshot.externalSourceList,
                viewOnly: !canEditSnapshot(snapshot.accountUuid),
                handler: async (options: ISaveSnapshotProps): Promise<unknown> => {
                    const { apiAccountId, selectedPackages, useExternalSettings, externalSourceList, ...releaseOptions } = options;
                    
                    return snapshotStore.updateSnapshot(apiAccountId, snapshot.id, {
                        useExternalSettings, 
                        externalSourceList: trimExternalSourceList(externalSourceList),
                        ...releaseOptions,
                        removePackageIds: without(snapshot.packageIds, ...selectedPackages),
                        addPackageIds: without(selectedPackages, ...snapshot.packageIds),
                    });
                },
            };
        }

        return {
            viewOnly: false,
            title: 'Add new software release',
            defaultMetapackages: snapshotStore.getMetaPackagesInRelease(undefined),
            changeableTargetPackages: [],
            useExternalSettings:  false,
            externalSourceList: undefined,
            handler: async (options: ISaveSnapshotProps): Promise<unknown> => {
                const { apiAccountId, selectedPackages, useExternalSettings, externalSourceList, ...releaseOptions } = options;
                return snapshotStore.createSnapshot(apiAccountId, {
                    useExternalSettings,
                    externalSourceList: trimExternalSourceList(externalSourceList),
                    ...releaseOptions,
                    packageIds: selectedPackages
                });
            },
        };
    }, [snapshotId, snapshotStore.dataLoading]);

    if (snapshotDetails.viewOnly) {
        return (
            <SpiningComponent
                child={
                    <ViewSnapshotPackageComponent
                        snapshotId={snapshotId}
                        title="Software release details"
                        footerButtonProps={{
                            type: 'primary',
                            label: 'Back to created releases',
                            onClick: context.toCreatedSnapshotTable,
                        }}
                    />
                }
            />
        );
    }

    const onSaveSnapshot = async (options: ISaveSnapshotProps): Promise<void> => {
        const packages: TDisplayPackageProps[] = [];

        // Merge all packages in base releases to check duplicated and detect dependency
        packages.push(...packagesStore.getPackagesByIds(options.selectedPackages));
        packages.push(...snapshotStore.getPackagesBySnapshotId(options.almReleaseId));
        packages.push(...snapshotStore.getPackagesBySnapshotId(options.vosReleaseId));
        packages.push(...snapshotStore.getPackagesBySnapshotId(options.scrReleaseId));
        
        const handleSave = async (): Promise<void> => {
            spinningStates.onStart();
            try {
                await snapshotDetails.handler(options);
                context.toCreatedSnapshotTable();
            } catch (err) {
                notification.error({
                    message: 'Error',
                    description: getErrorMessage(err),
                    placement: 'topRight'
                });
            } finally {
                spinningStates.onFinish();
            }
        };

        if (showDuplicatePackagesModal({ packages })) {
            return;
        }
        if (showMissingPackageDependenciesModal({ missingPackageDependencies: packagesStore.detectDependencyPackages(packages.map(item => item.id)) }, () => {
            confirmDialog('There are missing package dependencies. Are you sure you want to continue?', async () => {
                await handleSave();
            });
        })) {
            return;
        }
        await handleSave();
    };

    return (
        <SpiningComponent
            child={
                <CreateOrEditSnapshotComponent
                    {...snapshotDetails}
                    changeableTargetPackages={snapshotDetails?.changeableTargetPackages}
                    loading={snapshotStore.dataLoading || packagesStore.dataLoading}
                    onSaveSnapshot={onSaveSnapshot}
                    onCancel={(): void => confirmCancel(context.toCreatedSnapshotTable)}
                />
            }
        />
    );
});

export const CreateOrEditSnapshotComponent = observer((props: IAddPackageProps) => {
    const [form] = useForm();
    const { snapshotStore, packagesStore, accountStore, accountConfigurationStore } = useStores();
    const { currentUser } = useAuthStore();
    const [basedRelease, setBasedSnapshot] = React.useState<ISnapshotProps>(() => snapshotStore.getSnapshot(props.basedReleaseId));

    const metaPackagesOfRelease = React.useMemo(() => {
        if (basedRelease?.id === props.basedReleaseId) {
            return props.defaultMetapackages;
        }
        return snapshotStore.getMetaPackagesInRelease(basedRelease?.id);
    }, [basedRelease]);

    const organisationSource = React.useMemo<ITreeSelectDataSource>(
        () => accountStore.getSelectableTreeDataSource(Object.values(AccountType), (
            currentUser.accountType === AccountType.VAR ? [AccountType.CLIENT, AccountType.VAR] : [AccountType.CLIENT]
        )),
        [currentUser],
    );

    const [selectedClientAccount, setSelectedClientAccount] = React.useState<IAccount>(() => {
        if (props.accountUuid) {
            return accountStore.getAccountByUuid(props.accountUuid);
        }
        return currentUser.accountType === AccountType.CLIENT ? accountStore.getAccountById(currentUser.accountId) : null;
    });

    const basedSnapshotList = React.useMemo(() => {
        return snapshotStore.getSnapshotsByAccountUuid(selectedClientAccount?.uuid);
    }, [selectedClientAccount, snapshotStore.displayPublishedSnapshots]);

    React.useEffect(() => {
        const defaultSelectedBasedRelease = basedSnapshotList[0];
        if (!props.basedReleaseId) {
            setBasedSnapshot(defaultSelectedBasedRelease);
            return;
        }
        setBasedSnapshot(basedSnapshotList.find(item => item.id === props.basedReleaseId) ?? defaultSelectedBasedRelease);
    }, [basedSnapshotList]);

    const varAndClientPackages = React.useMemo<TDisplayPackageProps[]>(
        () => {
            const packages = packagesStore.displayPackages.filter(pkg => AVAILABLE_PACKAGE_TYPES.includes(pkg.type));
            if (!selectedClientAccount) {
                return packages;
            }
            return packagesStore.displayPackages
                .filter(pkg => pkg.accountUuid === selectedClientAccount.uuid || pkg.accountUuid === selectedClientAccount.parent.uuid);
        },
        [selectedClientAccount, packagesStore.displayPackages]
    );

    const changeableTargetPackages = React.useMemo(() => {
        return props.changeableTargetPackages?.filter(item => varAndClientPackages.some(pkg => pkg.id === item)).map(String) ?? [];
    }, [props.changeableTargetPackages]);

    React.useEffect(() => {
        if (!basedRelease || basedRelease.id === props.basedReleaseId) { // Priority should be given to obtaining the current selected packages.
            setTargetPackageKeys(changeableTargetPackages ?? []);
            setUseExternalSettings(props.useExternalSettings ?? false);
            dispatchExternalSourceList({ type: 'SET_EXTERNAL_SOURCELIST', payload: isNullOrEmpty(props.externalSourceList) ? initialExternalSourceList : props.externalSourceList });
        } else {
            setTargetPackageKeys(basedRelease.packageIds.map(String));
            setUseExternalSettings(basedRelease.useExternalSettings);
            dispatchExternalSourceList({ type: 'SET_EXTERNAL_SOURCELIST', payload: isNullOrEmpty(basedRelease.externalSourceList) ? initialExternalSourceList : basedRelease.externalSourceList });
        }
    }, [basedRelease]);

    const almBase = useMetaPackageStates(METAPACKAGE_TYPE.ALM, metaPackagesOfRelease);
    const scrBase = useMetaPackageStates(METAPACKAGE_TYPE.SCR, metaPackagesOfRelease);
    const vosBase = useMetaPackageStates(METAPACKAGE_TYPE.VOS, metaPackagesOfRelease);

    const reducer = (previousState: string[], selectedPackageIds: string[]): string[] => {
        return selectedPackageIds.filter(item => varAndClientPackages.some(pkg => pkg.id === toNumber(item)));
    };

    const [targetPackageKeys, setTargetPackageKeys] = React.useReducer(reducer, changeableTargetPackages);
    const [selectedKeys, setSelectedKeys] = React.useState<string[]>();

    const [useExternalSettings, setUseExternalSettings] = React.useState<boolean>(props.useExternalSettings ?? false);

    const initialExternalSourceList: IExternalSourceListProps = {
        repositorySettings: {
            options: [],
            distribution: '',
            target: '',
            component: '',
        },
        externalRepoAccountUuid: '',
        installPackages: [],
        removePackages: [],
    };

    type ExternalSettingsAction =
        | { type: 'SET_EXTERNAL_SOURCELIST'; payload: Partial<IExternalSourceListProps> }
        | { type: 'SET_REPOSITORY_SETTINGS'; payload: Partial<IRepositorySettingsProps> }
        | { type: 'SET_INSTALL_PACKAGES'; payload: string[] }
        | { type: 'SET_REMOVE_PACKAGES'; payload: string[] }
        | { type: 'CLEAR' };

    const externalSourceListReducer = (state: IExternalSourceListProps, action: ExternalSettingsAction): IExternalSourceListProps => {
        switch (action.type) {
            case 'SET_EXTERNAL_SOURCELIST':
                return {
                    ...state,
                    ...action.payload,
                };
            case 'SET_REPOSITORY_SETTINGS':
                return {
                    ...state,
                    repositorySettings: {
                        ...state.repositorySettings,
                        ...action.payload,
                    },
                };
            case 'SET_INSTALL_PACKAGES':
                return {
                    ...state,
                    installPackages: action.payload,
                };
            case 'SET_REMOVE_PACKAGES':
                return {
                    ...state,
                    removePackages: action.payload,
                };
            case 'CLEAR':
                return initialExternalSourceList;
            default:
                return state;
        }
    };

    const [externalSourceList, dispatchExternalSourceList] = React.useReducer(externalSourceListReducer, props.externalSourceList ?? initialExternalSourceList);

    const externalRepoSettings = usePromiseMemo(undefined, async () => {
        if (!selectedClientAccount?.id) {
            return undefined;
        }
        return await accountConfigurationStore.getExternalRepoSettings(selectedClientAccount.id, selectedClientAccount.uuid, { parentAccountUuid: selectedClientAccount.parent.uuid }).catch((err) => undefined);
    }, [selectedClientAccount]);  

    const isConfigured = React.useMemo(() => {
        return !isNullOrEmpty(externalRepoSettings?.repositoryURL);
    }, [externalRepoSettings]);

    const externalUrl = usePromiseMemo(undefined, async () => {
        return externalRepoSettings?.repositoryURL;
    }, [externalRepoSettings]);

    const isExternalVisible = React.useMemo(() => {
        return isConfigured && useExternalSettings;
    }, [isConfigured, useExternalSettings]);

    const repositoryString = React.useMemo(() => {
        if(!externalUrl){
            return '';
        }
        const { target, distribution, component, options } = externalSourceList.repositorySettings;
        if (!target && !distribution && !component) {
            return '';
        }
        const optionsString = options.length > 1 || (options.length == 1 && options[0] != '')  ? `[${options.join(',')}] ` : '';
        return `deb ${optionsString}${externalUrl}/${target} ${distribution} ${component}`;
    }, [externalSourceList, externalUrl]);

    React.useEffect(() => {
        form.setFieldsValue({
            'external_repository': repositoryString,
            'external_target': externalSourceList?.repositorySettings?.target,
            'external_distribution': externalSourceList?.repositorySettings?.distribution,
            'external_component': externalSourceList?.repositorySettings?.component,
            'external_options': externalSourceList?.repositorySettings?.options.join(','),
            'external_install': externalSourceList?.installPackages?.join(','),
            'external_remove': externalSourceList?.removePackages?.join(','),
        });
    }, [repositoryString, externalSourceList]);

    const transferTableColumns: IBasicTableColumn[] = [
        {
            code: 'label',
            title: 'Package Name',
            dataIndex: 'label',
            width: '45%',
            sorter: (a: TDisplayPackageProps, b: TDisplayPackageProps) => a.label?.localeCompare(b.label),
            onFilter: (text: string, record: TDisplayPackageProps) => hasContainsIgnoreCase(record.label, text),
            textSearchPlaceholder: 'Package label',
            iconName: 'search',
        },
        {
            code: 'version',
            title: 'Version',
            dataIndex: 'version',
            width: '20%',
        },
        {
            code: 'architecture',
            title: 'Architecture',
            dataIndex: 'architecture',
            width: '10%',
            sorter: (a: TDisplayPackageProps, b: TDisplayPackageProps) => a.architecture?.localeCompare(b.architecture),
            filters: Object.keys(PACKAGE_ARCH).map((arch) => ({ text: PACKAGE_ARCH[arch], value: PACKAGE_ARCH[arch] })),
            onFilter: (architecture, pkg) => pkg.architecture == architecture,
        },
        {
            code: 'repositoryLabel',
            title: 'Repository',
            dataIndex: 'repositoryLabel',
            width: '20%',
            textSearchPlaceholder: 'Repository',
            onFilter: (repositoryLabel, pkg) => hasContainsIgnoreCase(pkg.repositoryLabel, repositoryLabel),
            sorter: (a: TDisplayPackageProps, b: TDisplayPackageProps) => a.repositoryLabel?.localeCompare(b.repositoryLabel),
        },
        {
            code: 'createdAt',
            title: 'Created Date',
            dataIndex: 'createdAt',
            defaultSortOrder: 'descend',
            sorter: (a: TDisplayPackageProps, b: TDisplayPackageProps) => a.createdAtTimestamp?.localeCompare(b.createdAtTimestamp),
        },
    ];

    // eslint-disable-next-line react/display-name
    const MemorizedBasedSnapshot = React.memo<Pick<ISelectItemProps, 'selectedValue' | 'dataSource' | 'onChange'>>(({
        selectedValue,
        dataSource,
        onChange
    }) => {
        const basedSnapshotCode = 'based-snapshot';
        React.useEffect(() => {
            form.setFieldsValue({ [basedSnapshotCode]: selectedValue });
        }, [selectedValue]);

        return (<SearchableSelectFormItem
            code={basedSnapshotCode}
            label="Based on"
            labelAlign="right"
            isRequired={!isNullOrEmpty(dataSource)}
            selectedValue={selectedValue}
            dataSource={dataSource}
            onChange={onChange}
        />);
    }, (prevProps, nextProps) => isEqual(prevProps.dataSource, nextProps.dataSource) && isEqual(prevProps.selectedValue, nextProps.selectedValue));

    const MetaPackageSelectComponent = ({
        code,
        label,
        source,
        packages,
        selectedItem,
        description,
        onChange
    }: IMetaPackageComponentProps): JSX.Element => {
        React.useEffect(() => {
            form.setFieldsValue({ [code]: selectedItem });
        }, [selectedItem]);
        
        return (
            <React.Fragment>
                <SelectFormItem
                    className={packagesCss['no-margin-form-item']}
                    key={code}
                    code={code}
                    label={label}
                    labelAlign='right'
                    isRequired={true}
                    selectedValue={selectedItem}
                    dataSource={source.map((item) => ({
                        value: item.id,
                        label: `${item.label}-${item.version}`,
                        tooltip: item.description ? item.description : `${item.label}-${item.version}`,
                        placement: 'bottomLeft',
                    }))}
                    onChange={onChange}
                    extraComponent={
                        <Typography.Text key='description' className={packagesCss['description-center']}>
                            Description: {description}
                        </Typography.Text>
                    }
                />
                <FormItem
                    label=' '
                    code={`${code}-link`}
                    labelAlign='right'
                    labelCol={{ span: 5 }}
                    colon={false}
                    child={
                        <Typography.Link
                            disabled={isNullOrEmpty(packages)}
                            onClick={(): unknown => showPackagesWithDescriptionModal(packages, description)}
                        >
                            View packages
                        </Typography.Link>
                    }
                />
            </React.Fragment>
        );
    };
   
    return (
        <BasicForm
            scrollToFirstError={true}
            form={form}
            onSubmit={(values): unknown => {
                if (!selectedClientAccount?.id) {
                    return notification.error({
                        message: 'Error',
                        description: 'Invalid client account',
                        placement: 'topRight',
                    });
                }
                return props.onSaveSnapshot({
                    apiAccountId: selectedClientAccount?.id,
                    label: values['snapshotName'],
                    description: values['description'] ?? undefined,
                    accountUuid: selectedClientAccount?.uuid,
                    basedReleaseId: basedRelease?.id,
                    almReleaseId: almBase.selectedItem,
                    vosReleaseId: vosBase.selectedItem,
                    scrReleaseId: scrBase.selectedItem,
                    selectedPackages: targetPackageKeys.map(Number),
                    useExternalSettings: useExternalSettings,
                    externalSourceList: {
                        repositorySettings: {
                            options: externalSourceList.repositorySettings.options,
                            distribution: externalSourceList.repositorySettings.distribution,
                            target: externalSourceList.repositorySettings.target,
                            component: externalSourceList.repositorySettings.component,
                        },
                        externalRepoAccountUuid:externalRepoSettings?.externalRepoAccountUuid,
                        installPackages: externalSourceList.installPackages,
                        removePackages: externalSourceList.removePackages,
                    },
                });
            }}
            items={[
                <React.Fragment key='snapshot-fragment'>
                    <Typography.Title className={packagesCss.title} level={4}>
                        {props.title}
                    </Typography.Title>
                    <TextFieldItem
                        code='snapshotName'
                        label='Name'
                        key='snapshotName'
                        labelAlign='right'
                        isRequired={true}
                        initialValue={props.name || ''}
                    />

                    <TextAreaFieldItem
                        code='description'
                        label='Description'
                        labelAlign='right'
                        textAreaProps={{
                            autoSize: { minRows: 2, maxRows: 5 },
                        }}
                        initialValue={props.description}
                    />
                    <OrganisationSelectItem
                        code='organisation'
                        label='Organisation'
                        labelAlign='right'
                        isRequired={true}
                        hidden={currentUser.accountType === AccountType.CLIENT}
                        selectProps={{
                            treeDataSource: organisationSource,
                            placeholder: 'Organisation',
                            expandAll: true,
                            onChange: (seletedId: number): void => {
                                setSelectedClientAccount(accountStore.getAccountById(seletedId));
                            },
                            value: selectedClientAccount?.id,
                        }}
                    />
                    <MemorizedBasedSnapshot
                        selectedValue={basedRelease?.id}
                        dataSource={basedSnapshotList.map(buildItemFn)}
                        onChange={(id: number): void => {
                            setBasedSnapshot(snapshotStore.getSnapshot(id));
                        }}
                    />
                    <MetaPackageSelectComponent code='alm-base' label='ALM Release' {...almBase} />
                    <MetaPackageSelectComponent code='vos-base' label='VOS Release' {...vosBase} />
                    <MetaPackageSelectComponent code='scr-base' label='SCR Release' {...scrBase} />
                    <SwitchSelectFormItem
                        label='Enable External Settings'
                        code='enable'
                        hidden={!isConfigured}
                        labelAlign='right'
                        switchSelectProps={{
                            checked: useExternalSettings,
                            onChange: (checked: boolean): void => {
                                setUseExternalSettings(checked);
                            },
                        }}
                    />
                    <TextFieldItem
                        code='external_repository'
                        label='Repository'
                        key='Repository'
                        labelAlign='right'
                        disabled={true}
                        hidden={!isExternalVisible}
                    /> 
                    <TextFieldItem
                        code='external_target'
                        label='Target'
                        key='Target'
                        labelAlign='right'
                        hidden={!isExternalVisible}
                        onChange={(ev): void => {
                            dispatchExternalSourceList({ type: 'SET_REPOSITORY_SETTINGS', payload: { target: ev.target.value } });
                        }}
                    />
                    <TextFieldItem
                        code='external_distribution'
                        label='Distribution'
                        key='Distribution'
                        labelAlign='right'
                        isRequired={isExternalVisible}
                        hidden={!isExternalVisible}
                        onChange={(ev): void => {
                            dispatchExternalSourceList({ type: 'SET_REPOSITORY_SETTINGS', payload: { distribution: ev.target.value } });
                        }}
                    />
                    <TextFieldItem
                        code='external_component'
                        label='Component'
                        key='Component'
                        labelAlign='right'
                        isRequired={isExternalVisible}
                        hidden={!isExternalVisible}
                        onChange={(ev): void => {
                            dispatchExternalSourceList({ type: 'SET_REPOSITORY_SETTINGS', payload: { component: ev.target.value } });
                        }}
                    />
                    <TextFieldItem
                        code='external_options'
                        label='Options'
                        key='Options'
                        labelAlign='right'
                        hidden={!isExternalVisible}
                        onChange={(ev): void => {
                            dispatchExternalSourceList({ type: 'SET_REPOSITORY_SETTINGS', payload: { options: ev.target.value.split(',') } });
                        }}
                    />
                    <TextFieldItem
                        code='external_install'
                        label='Install Packages'
                        key='Install_Packages'
                        labelAlign='right'
                        isRequired={isExternalVisible}
                        hidden={!isExternalVisible}
                        onChange={(ev): void => {
                            dispatchExternalSourceList({ type: 'SET_INSTALL_PACKAGES', payload: ev.target.value.split(',') });
                        }}
                    />
                    <TextFieldItem
                        code='external_remove'
                        label='Remove Packages'
                        key='Remove_Packages'
                        labelAlign='right'
                        hidden={!isExternalVisible}
                        onChange={(ev): void => {
                            dispatchExternalSourceList({ type: 'SET_REMOVE_PACKAGES', payload: ev.target.value.split(',') });
                        }}
                    />
                    <BasicCard
                        code='packagesTransferCard'
                        title='Select packages for the software release'
                        loading={props.loading}
                        bordered={false}
                        contents={[
                            <TransferWithContentTable
                                key='packages-transfer-table'
                                code='packagesTransfer'
                                className={packagesCss.transfer}
                                listStyle={{
                                    minHeight: 400,
                                    width: 500,
                                }}
                                dataSource={varAndClientPackages}
                                targetKeys={targetPackageKeys}
                                selectedKeys={selectedKeys}
                                operations={['Add', 'Remove']}
                                titles={[
                                    <Label key='availablePackagesLabel' label='Available Packages' strong />,
                                    <Label key='previousSnapshotLabel' label='Selected Packages' strong />,
                                ]}
                                onSelectChange={(sourceSelectedKeys: string[], targetSelectedKeys: string[]): void =>
                                    setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys])
                                }
                                onChange={(targetKeys: string[], direction: string): void => {
                                    if (direction === 'left') {
                                        confirmDialog('Are you sure you want to remove this package?', () =>
                                            setTargetPackageKeys(targetKeys),
                                        );
                                        return;
                                    }
                                    setTargetPackageKeys(targetKeys);
                                }}
                                filterOption={(inputValue: string, item: TDisplayPackageProps): boolean =>
                                    item.label.toLowerCase().includes(inputValue.toLowerCase())
                                }
                                showSearch
                                columns={transferTableColumns}
                                tableExtraOptions={{ scroll: { x: '100%' } }}
                            />,
                        ]}
                    />

                    <div className={packagesCss.row}>
                        <IconTextButton key='cancel' label='Cancel' onClick={props.onCancel} />
                        <IconTextButton key='save' label='Save' type='primary' htmlType='submit' />
                    </div>
                </React.Fragment>,
            ]}
        />
    );
});
